// Lib
import { Matrix3D } from 'rematrix';
import { clamp } from 'lodash';

// Utils
import * as rectLib from '../../../../common/maths/geometry/rect';
import * as pointLib from '../../../../common/maths/geometry/point';
import * as matrixLib from '../../../../common/maths/matrix/matrixUtils';

// Types
import { Rect, RectSize } from '../../../../common/maths/geometry/rect/rectTypes';
import { Point } from '../../../../common/maths/geometry/pointTypes';

// Constants
import { ZOOM_PROPS } from '../zoomConstants';
import { ZoomPointMode } from '../zoomPointModeUtils';

const MAXIMUM_SCALING_FACTOR = 1.3;

/**
 * Gets the target scale based on the current scale and the scale when "fit".
 */
export const getTargetZoomToFitScale = (currentScale: number, zoomToFitScale: number): number => {
    // If the current scale is really close to the zoom to scale ratio, then return to 100%;
    if (Math.abs(currentScale - zoomToFitScale) < 0.01) return 1;

    // If the current scale is less than 1 and the zoomToFitScale is less than 1 but greater than the current zoom
    // just return to 1
    if (currentScale < 1 && currentScale < zoomToFitScale && zoomToFitScale < 1) return 1;

    // If the current scale is greater than 1 and the zoomToFitScale is greater than 1 but less than the current zoom
    // just return to 1
    if (currentScale > 1 && currentScale > zoomToFitScale && zoomToFitScale > 1) return 1;

    return zoomToFitScale;
};

/**
 * Gets the scale to correctly fit the content into view.
 */
export const getZoomToFitScale = (contentRect: Rect, canvasViewportSize: RectSize): number => {
    const widthRatio = canvasViewportSize.width / contentRect.width;
    // The height of the canvas will remain as is
    const heightRatio = canvasViewportSize.height / contentRect.height;

    const minRatio = Math.min(widthRatio, heightRatio);

    // If we're zooming into the content then zoom in to a maximum of 130%
    if (minRatio > 1) return Math.min(minRatio, MAXIMUM_SCALING_FACTOR);

    // Otherwise limit to a minimum of 10% and a maximum of 80%
    return clamp(minRatio, ZOOM_PROPS.MIN_SCALE, ZOOM_PROPS.MIN_SCALE_MAX);
};

/**
 * Gets the zoom point mode to use for a zoom to fit operation.
 */
export const getZoomToFitZoomPointMode = (
    currentScale: number,
    targetScale: number,
    zoomCanvasTransformationPoint: Point,
    cachedTransformationPoint: Point,
    contentRect: Rect,
    prevContentRect?: Rect,
): ZoomPointMode => {
    if (targetScale !== 1) return ZoomPointMode.CENTRED;

    const shouldUseCachedTransformationPoint =
        // Only use the cached transformation point if we're currently less than 100%
        // It doesn't work when zoomed in
        currentScale < 1 &&
        // And there hasn't been any manual zooms since then
        zoomCanvasTransformationPoint === cachedTransformationPoint &&
        // And the content that we're zooming to fit hasn't changed in size
        rectLib.isEqual(prevContentRect, contentRect);

    if (shouldUseCachedTransformationPoint) return ZoomPointMode.CACHED_LIMITED;

    return currentScale > 1 ? ZoomPointMode.EVENT_POS_LIMITED_ZOOMED_IN : ZoomPointMode.EVENT_POS_LIMITED_ZOOMED_OUT;
};

/**
 * Determines the event position to use when zooming to fit, based on the current zoom mode.
 */
export const getZoomToFitEventPosition = (
    zoomCanvasTransformationPoint: Point,
    cachedTransformationPoint: Point,
    zoomPointMode: ZoomPointMode,
): Point => {
    // Doesn't really matter what we return here
    if (zoomPointMode === ZoomPointMode.CACHED) return cachedTransformationPoint;
    if (zoomPointMode === ZoomPointMode.CACHED_LIMITED) return cachedTransformationPoint;

    // If a manual zoom has caused the transformation point in the zoom canvas to be different to the zoom to fit
    // cached point, then use the one from the zoom canvas as the event position
    if (zoomCanvasTransformationPoint !== cachedTransformationPoint) return zoomCanvasTransformationPoint;

    // Otherwise, just use the top left corner of the board to return to
    return { x: 0, y: 0 };
};

/**
 * Gets new canvas scroll based on the new translation and existing scroll.
 */
const getNewCanvasScroll = (transformationMatrix: Matrix3D, canvasScroll: Point): Point => {
    const currentScale = matrixLib.getScale(transformationMatrix);
    const currentTranslation = matrixLib.getTranslation(transformationMatrix);

    // FIXME This should potentially depend on where the zoom to fit starts from?
    if (currentScale !== 1) return { x: 0, y: 0 };

    // Otherwise scroll should reverse the current translation
    return pointLib.difference(currentTranslation, canvasScroll);
};

/**
 * Changes the transformation matrix based on the change in scroll and translation.
 */
const getNewTransformationMatrix = (
    transformationMatrix: Matrix3D,
    prevCanvasScroll: Point,
    newCanvasScroll: Point,
): Matrix3D => {
    const currentScale = matrixLib.getScale(transformationMatrix);

    // If we're returning to 100% the matrix's translation will be replaced with scroll,
    // so just set the translation to 0
    if (currentScale === 1) return matrixLib.setTranslation(transformationMatrix, { x: 0, y: 0 });

    const currentTranslation = matrixLib.getTranslation(transformationMatrix);

    // Otherwise, account for the scroll change in the matrix's translation
    const scrollDiff = pointLib.difference(newCanvasScroll, prevCanvasScroll);
    const newTranslation = pointLib.difference(scrollDiff, currentTranslation);

    return matrixLib.setTranslation(transformationMatrix, newTranslation);
};

/**
 * Determines the new scroll area size when we're zooming to fit.
 */
const getNewScrollAreaSize = (
    transformationMatrix: Matrix3D,
    canvasViewportClientRect: RectSize | null,
    canvasDocumentSize: RectSize,
    contentRect: Rect,
): RectSize => {
    const currentScale = matrixLib.getScale(transformationMatrix);

    // If we're scaled to fit then only match the viewport or content rect size
    if (currentScale !== 1) {
        const scaledContentRect: Rect = rectLib.scale(currentScale, contentRect);

        // FIXME If the scroll area size is almost identical to the viewport size, just use the viewport size
        return {
            width: Math.max(scaledContentRect.width, canvasViewportClientRect?.width || 0),
            height: Math.max(scaledContentRect.height, canvasViewportClientRect?.height || 0),
        };
    }

    // Otherwise match the canvas document
    return canvasDocumentSize;
};

/**
 * Shifts the transformation point based on changes to the scroll and matrix transformation
 * so that future zooms using the cached transformation point will still be correct.
 */
const getNewTransformationPoint = (
    transformationPoint: Point,
    prevCanvasScroll: Point,
    newCanvasScroll: Point,
): Point => {
    const scrollDiff = pointLib.difference(newCanvasScroll, prevCanvasScroll);
    return pointLib.difference(scrollDiff, transformationPoint);
};

interface ResetCanvasPropertiesResponse {
    transformationPoint: Point;
    transformationMatrix: Matrix3D;
    scrollAreaSize: RectSize;
    canvasScroll: Point;
}

/**
 * Determines the changes to various zoom related properties when the zoom is being "committed".
 */
export const calculateZoomToFitResetCanvasProperties = (
    transformationPoint: Point,
    transformationMatrix: Matrix3D,
    canvasViewportClientRect: DOMRect,
    canvasDocumentSize: RectSize,
    contentRect: Rect,
    canvasScroll: Point,
): ResetCanvasPropertiesResponse => {
    const newScrollAreaSize = getNewScrollAreaSize(
        transformationMatrix,
        canvasViewportClientRect,
        canvasDocumentSize,
        contentRect,
    );

    const newCanvasScroll = getNewCanvasScroll(transformationMatrix, canvasScroll);

    const newTransformationMatrix = getNewTransformationMatrix(transformationMatrix, canvasScroll, newCanvasScroll);
    const newTransformationPoint = getNewTransformationPoint(transformationPoint, canvasScroll, newCanvasScroll);

    return {
        transformationPoint: newTransformationPoint,
        transformationMatrix: newTransformationMatrix,
        scrollAreaSize: newScrollAreaSize,
        canvasScroll: newCanvasScroll,
    };
};
